import { useCallback, useMemo, type ReactNode } from 'react';
import { Button, Dropdown, Tooltip, type MenuProps } from 'antd';
import { DesktopOutlined, MoonFilled, SunFilled } from '@ant-design/icons';

import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import type { ThemeMode } from '@store/slices/uiSlice.ts';

interface ThemeToggleProps {
  size?: 'small' | 'middle' | 'large';
  withLabel?: boolean;
  className?: string;
}

const LABELS: Record<ThemeMode, string> = {
  light: 'Claro',
  dark: 'Oscuro',
  system: 'Sistema',
};

const ICONS: Record<'light' | 'dark' | 'system', ReactNode> = {
  light: <SunFilled />,
  dark: <MoonFilled />,
  system: <DesktopOutlined />,
};

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ size = 'middle', withLabel = false, className }) => {
  const { themeMode, resolvedTheme, setThemeMode } = useThemeMode();

  const items = useMemo<MenuProps['items']>(
    () => [
      {
        key: 'light',
        icon: ICONS.light,
        label: 'Claro',
      },
      {
        key: 'dark',
        icon: ICONS.dark,
        label: 'Oscuro',
      },
      {
        key: 'system',
        icon: ICONS.system,
        label: 'Sistema',
      },
    ],
    [],
  );

  const handleMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      setThemeMode(key as ThemeMode);
    },
    [setThemeMode],
  );

  const buttonLabel = LABELS[themeMode];
  const ariaLabel = `Cambiar tema. Modo actual: ${buttonLabel}`;

  const button = (
    <Button
      size={size}
      className={className}
      icon={ICONS[themeMode === 'system' ? resolvedTheme : themeMode]}
      aria-haspopup="menu"
      aria-label={withLabel ? undefined : ariaLabel}
      type="default"
    >
      {withLabel ? buttonLabel : null}
    </Button>
  );

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{ items, selectable: true, selectedKeys: [themeMode], onClick: handleMenuClick }}
    >
      {withLabel ? button : <Tooltip title={ariaLabel} placement="left">{button}</Tooltip>}
    </Dropdown>
  );
};

export default ThemeToggle;
